import { Controller } from "@hotwired/stimulus";
import {
  computeBoxDimensions,
  computeDepthFromCm,
  computeSideUvs,
  toLandscape
} from "../configurator/preview_geometry";
import {
  TEXTURE_SIZE,
  ROUNDED_RADIUS_RATIO,
  computeTextureSize,
  computePhotoRect,
  computeCoverSourceRect,
  computeContainDestRect
} from "../configurator/preview_texture";

// three.js is ~500 KiB minified and only this controller needs it, so it is
// loaded on demand instead of shipping in application.js on every page.
let THREE;
const loadThree = async () => (THREE ??= await import("three"));

const PRINT_DEPTH_UNITS = 0.01;
const MAGNETKA_DEPTH_UNITS = 0.04;
const BACK_COLOR = 0xe8e6e0;
const SIDE_COLOR = 0xffffff;

class PhotoTextureBuilder {
  constructor() {
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d");
  }

  render({ image, dimAspect, cropMode, whiteFrame, roundedCorners }) {
    const { width, height } = computeTextureSize({ dimAspect });
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    const ctx = this.ctx;
    ctx.save();
    ctx.clearRect(0, 0, width, height);

    if (roundedCorners) {
      const radius = Math.min(width, height) * ROUNDED_RADIUS_RATIO;
      ctx.beginPath();
      ctx.roundRect(0, 0, width, height, radius);
      ctx.clip();
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    if (image) {
      const photoRect = computePhotoRect({ canvasWidth: width, canvasHeight: height, whiteFrame });
      if (cropMode === "fill") {
        const src = computeCoverSourceRect({
          imageWidth: image.naturalWidth,
          imageHeight: image.naturalHeight,
          destWidth: photoRect.w,
          destHeight: photoRect.h
        });
        ctx.drawImage(image, src.sx, src.sy, src.sw, src.sh,
          photoRect.x, photoRect.y, photoRect.w, photoRect.h);
      } else {
        const dst = computeContainDestRect({
          imageWidth: image.naturalWidth,
          imageHeight: image.naturalHeight,
          destX: photoRect.x,
          destY: photoRect.y,
          destWidth: photoRect.w,
          destHeight: photoRect.h
        });
        ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight,
          dst.dx, dst.dy, dst.dw, dst.dh);
      }
    }

    ctx.restore();
  }
}

export default class extends Controller {
  static targets = ["canvas", "fallback", "mirrorHint"];

  static values = {
    // Wording comes from the view so it follows the domain's language.
    hints: Object,
    photoUrl: String,
    dimensionOptionTypeId: { type: Number, default: 0 },
    dimensionValues: { type: Object, default: {} },
    thicknessOptionTypeId: { type: Number, default: 0 },
    thicknessValues: { type: Object, default: {} },
    defaultThicknessCm: { type: Number, default: 4 },
    hasMirror: { type: Boolean, default: false },
    cropMode: { type: String, default: "fill" },
    hasWhiteFrame: { type: Boolean, default: false },
    hasRoundedCorners: { type: Boolean, default: false }
  };

  async connect() {
    if (!this.isWebGLAvailable()) {
      this.showFallback();
      return;
    }
    this.mirrored = this.hasMirrorValue;
    this.cropMode = this.cropModeValue;
    this.whiteFrame = this.hasWhiteFrameValue;
    this.roundedCorners = this.hasRoundedCornersValue;
    this.portrait = false;
    this.dimensions = this.orient(this.readInitialDimensions());
    this.thicknessCm = this.readInitialThicknessCm();
    this.rotation = { x: -0.08, y: -0.45 };
    this.targetRotation = { ...this.rotation };
    this.isDragging = false;
    this.lastPointer = null;
    this.textureBuilder = new PhotoTextureBuilder();

    // Option toggles may fire while three.js is still downloading; they only
    // update state here, and the scene picks it up once it is built.
    const token = (this.connectToken = {});
    try {
      await loadThree();
    } catch (error) {
      console.error("[product-preview] three.js failed to load", error);
      this.showFallback();
      return;
    }
    if (this.connectToken !== token || this.stopped) return;

    try {
      this.initScene();
      this.renderTexture();
      this.bindSelects();
      this.bindPointer();
      this.startLoop();
    } catch (error) {
      console.error("[product-preview] init failed", error);
      this.showFallback();
    }
  }

  disconnect() {
    this.connectToken = null;
    this.stopped = true;
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    if (this.resizeFrameHandle) cancelAnimationFrame(this.resizeFrameHandle);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.dimensionHandler && this.dimensionSelect) {
      this.dimensionSelect.removeEventListener("change", this.dimensionHandler);
    }
    if (this.thicknessHandler && this.thicknessSelect) {
      this.thicknessSelect.removeEventListener("change", this.thicknessHandler);
    }
    if (this.hasCanvasTarget && this.onPointerDown) {
      const canvas = this.canvasTarget;
      canvas.removeEventListener("pointerdown", this.onPointerDown);
      canvas.removeEventListener("pointermove", this.onPointerMove);
      canvas.removeEventListener("pointerup", this.onPointerUp);
      canvas.removeEventListener("pointercancel", this.onPointerUp);
      canvas.removeEventListener("pointerleave", this.onPointerLeave);
    }
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
    if (this.geometry) this.geometry.dispose();
    if (this.materials) this.materials.forEach((m) => m.dispose());
    if (this.canvasTexture) this.canvasTexture.dispose();
  }

  initScene() {
    const canvas = this.canvasTarget;
    const { width, height } = this.canvasSize();

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(28, width / height || 1, 0.1, 100);
    this.camera.position.set(0, 0, 12);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setSize(width, height, false);

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xdddddd, 2.8));
    const key = new THREE.DirectionalLight(0xffffff, 0.7);
    key.position.set(3, 4, 6);
    this.scene.add(key);

    this.group = new THREE.Group();
    this.scene.add(this.group);

    this.buildMesh();
    this.observeResize();
  }

  buildMesh() {
    if (!this.group) return;
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.geometry?.dispose();
    }
    const { w, h } = computeBoxDimensions(this.boxDimsInput);
    const d = this.currentDepth;

    this.geometry = new THREE.BoxGeometry(w, h, d);
    if (!this.materials) {
      this.materials = this.buildMaterials();
    }
    this.applySideUvs({ w, h, d });
    this.mesh = new THREE.Mesh(this.geometry, this.materials);
    this.group.add(this.mesh);
  }

  buildMaterials() {
    const front = new THREE.MeshLambertMaterial({ color: 0xffffff, alphaTest: 0.5 });
    const side = () => new THREE.MeshLambertMaterial({ color: SIDE_COLOR });
    const back = new THREE.MeshLambertMaterial({ color: BACK_COLOR });
    // BoxGeometry material order: [+X, -X, +Y, -Y, +Z, -Z]
    return [side(), side(), side(), side(), front, back];
  }

  applySideUvs({ w, h, d } = {}) {
    if (!this.hasMirrorValue || this.stopped || !this.geometry) return;
    const uv = this.geometry.attributes.uv;
    const dims = w !== undefined ? { w, h } : computeBoxDimensions(this.boxDimsInput);
    const depth = d !== undefined ? d : this.currentDepth;
    const sideUvs = computeSideUvs({ mirrored: this.mirrored, w: dims.w, h: dims.h, d: depth });
    sideUvs.forEach((coords, faceIndex) => {
      const i = faceIndex * 4;
      uv.setXY(i + 0, coords[0][0], coords[0][1]);
      uv.setXY(i + 1, coords[1][0], coords[1][1]);
      uv.setXY(i + 2, coords[2][0], coords[2][1]);
      uv.setXY(i + 3, coords[3][0], coords[3][1]);
    });
    uv.needsUpdate = true;
  }

  loadImage() {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = this.photoUrlValue;
    });
  }

  async renderTexture() {
    if (this.stopped || !this.renderer) return;
    if (!this.image) {
      try {
        this.image = await this.loadImage();
      } catch (error) {
        console.warn("[product-preview] image load failed", error);
        return;
      }
    }
    if (this.stopped) return;
    const dimAspect = this.dimensions.w / this.dimensions.h;
    this.textureBuilder.render({
      image: this.image,
      dimAspect,
      cropMode: this.cropMode,
      whiteFrame: this.whiteFrame,
      roundedCorners: this.roundedCorners
    });
    const { width: texW, height: texH } = this.textureBuilder.canvas;
    if (this.canvasTexture && (this.lastTextureSize?.width !== texW || this.lastTextureSize?.height !== texH)) {
      this.canvasTexture.dispose();
      this.canvasTexture = null;
    }
    this.lastTextureSize = { width: texW, height: texH };
    if (!this.canvasTexture) {
      this.canvasTexture = new THREE.CanvasTexture(this.textureBuilder.canvas);
      this.canvasTexture.colorSpace = THREE.SRGBColorSpace;
      this.canvasTexture.wrapS = THREE.MirroredRepeatWrapping;
      this.canvasTexture.wrapT = THREE.MirroredRepeatWrapping;
      this.canvasTexture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      this.assignTextureToMaterials();
    } else {
      this.canvasTexture.needsUpdate = true;
    }
  }

  assignTextureToMaterials() {
    if (!this.materials) return;
    this.materials[4].map = this.canvasTexture; // front face (+Z)
    this.materials[4].needsUpdate = true;
    if (this.hasMirrorValue) {
      [0, 1, 2, 3].forEach((i) => {
        this.materials[i].map = this.canvasTexture;
        this.materials[i].needsUpdate = true;
      });
    }
  }

  setCrop(event) {
    this.cropMode = event.target.value === "true" ? "fill" : "fit";
    this.renderTexture();
  }

  setFrame(event) {
    this.whiteFrame = event.target.checked;
    this.renderTexture();
  }

  resetFrame() {
    this.whiteFrame = false;
    this.renderTexture();
  }

  setRoundedCorner(event) {
    this.roundedCorners = event.target.checked;
    this.renderTexture();
  }

  setMirror(event) {
    if (!this.hasMirrorValue) return;
    this.mirrored = event.target.checked;
    this.updateMirrorHint();
    this.applySideUvs();
  }

  updateMirrorHint() {
    if (!this.hasMirrorHintTarget) return;
    const key = this.mirrored ? "mirror" : "wrap";
    this.mirrorHintTarget.textContent = this.hintsValue[key];
  }

  bindSelects() {
    if (this.dimensionOptionTypeIdValue) {
      this.dimensionSelect = document.querySelector(
        `select[data-option-type-id="${this.dimensionOptionTypeIdValue}"]`
      );
      if (this.dimensionSelect) {
        this.dimensionHandler = () => this.onDimensionChange();
        this.dimensionSelect.addEventListener("change", this.dimensionHandler);
      }
    }
    if (this.thicknessOptionTypeIdValue) {
      this.thicknessSelect = document.querySelector(
        `select[data-option-type-id="${this.thicknessOptionTypeIdValue}"]`
      );
      if (this.thicknessSelect) {
        this.thicknessHandler = () => this.onThicknessChange();
        this.thicknessSelect.addEventListener("change", this.thicknessHandler);
      }
    }
  }

  onDimensionChange() {
    const id = this.dimensionSelect.value;
    if (!id) return;
    const dims = this.dimensionValuesValue[id];
    if (!dims) return;
    const oriented = this.orient(dims);
    if (oriented.w === this.dimensions.w && oriented.h === this.dimensions.h) return;
    this.dimensions = oriented;
    this.buildMesh();
    this.renderTexture();
  }

  // Applies the current orientation to raw variant dimensions. Variants are
  // stored portrait-first, so the preview defaults to landscape and the
  // "Otočiť" button flips to portrait via this.portrait.
  orient(dims) {
    const landscape = toLandscape(dims);
    return this.portrait ? { w: landscape.h, h: landscape.w } : landscape;
  }

  onThicknessChange() {
    const id = this.thicknessSelect.value;
    if (!id) return;
    const cm = this.thicknessValuesValue[id];
    if (!cm || cm === this.thicknessCm) return;
    this.thicknessCm = cm;
    this.buildMesh();
  }

  toggleOrientation() {
    if (!this.dimensions) return;
    this.portrait = !this.portrait;
    this.dimensions = { w: this.dimensions.h, h: this.dimensions.w };
    this.buildMesh();
    this.renderTexture();
  }

  get boxDimsInput() {
    return { dimW: this.dimensions.w, dimH: this.dimensions.h };
  }

  get currentDepth() {
    if (this.hasMirrorValue) {
      return computeDepthFromCm({
        thicknessCm: this.thicknessCm,
        longestDimCm: Math.max(this.dimensions.w, this.dimensions.h)
      });
    }
    if (this.hasRoundedCornersValue) return MAGNETKA_DEPTH_UNITS;
    return PRINT_DEPTH_UNITS;
  }

  readInitialDimensions() {
    const fallback = { w: 40, h: 30 };
    if (!this.dimensionOptionTypeIdValue) return fallback;
    const select = document.querySelector(
      `select[data-option-type-id="${this.dimensionOptionTypeIdValue}"]`
    );
    if (!select || !select.value) return fallback;
    return this.dimensionValuesValue[select.value] || fallback;
  }

  readInitialThicknessCm() {
    if (!this.thicknessOptionTypeIdValue) return this.defaultThicknessCmValue;
    const select = document.querySelector(
      `select[data-option-type-id="${this.thicknessOptionTypeIdValue}"]`
    );
    if (!select || !select.value) return this.defaultThicknessCmValue;
    return this.thicknessValuesValue[select.value] || this.defaultThicknessCmValue;
  }

  bindPointer() {
    const canvas = this.canvasTarget;
    canvas.style.cursor = "grab";

    this.onPointerDown = (event) => {
      this.isDragging = true;
      this.lastPointer = { x: event.clientX, y: event.clientY };
      canvas.setPointerCapture?.(event.pointerId);
      canvas.style.cursor = "grabbing";
    };

    this.onPointerMove = (event) => {
      if (!this.isDragging) return;
      const dx = event.clientX - this.lastPointer.x;
      const dy = event.clientY - this.lastPointer.y;
      this.targetRotation.y += dx * 0.008;
      this.targetRotation.x += dy * 0.006;
      this.targetRotation.x = Math.max(-0.7, Math.min(0.7, this.targetRotation.x));
      this.lastPointer = { x: event.clientX, y: event.clientY };
    };

    this.onPointerUp = (event) => {
      this.isDragging = false;
      canvas.releasePointerCapture?.(event.pointerId);
      canvas.style.cursor = "grab";
    };

    this.onPointerLeave = () => {
      if (this.isDragging) {
        this.isDragging = false;
        canvas.style.cursor = "grab";
      }
    };

    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerUp);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
  }

  startLoop() {
    const tick = () => {
      if (this.stopped) return;
      this.rotation.x += (this.targetRotation.x - this.rotation.x) * 0.12;
      this.rotation.y += (this.targetRotation.y - this.rotation.y) * 0.12;
      this.group.rotation.x = this.rotation.x;
      this.group.rotation.y = this.rotation.y;
      this.renderer.render(this.scene, this.camera);
      this.frameHandle = requestAnimationFrame(tick);
    };
    tick();
  }

  observeResize() {
    this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
    this.resizeObserver.observe(this.canvasTarget.parentElement);
  }

  // Resizing the renderer mutates the canvas dimensions, which can resize the
  // observed parent again within the same frame — the browser then raises
  // "ResizeObserver loop completed with undelivered notifications" at
  // window.onerror. Deferring to the next animation frame breaks the loop and
  // coalesces observer bursts into a single resize.
  scheduleResize() {
    if (this.resizeFrameHandle) return;
    this.resizeFrameHandle = requestAnimationFrame(() => {
      this.resizeFrameHandle = null;
      this.handleResize();
    });
  }

  handleResize() {
    const { width, height } = this.canvasSize();
    if (width === 0 || height === 0) return;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  canvasSize() {
    const parent = this.canvasTarget.parentElement;
    return { width: parent.clientWidth, height: parent.clientHeight };
  }

  isWebGLAvailable() {
    try {
      const c = document.createElement("canvas");
      return !!(window.WebGLRenderingContext &&
        (c.getContext("webgl2") || c.getContext("webgl")));
    } catch (_e) {
      return false;
    }
  }

  // Puts the controller into a permanently inert state. Option toggles on the
  // form keep dispatching to this controller after the fallback kicks in, so
  // rendering must become a no-op rather than touch state connect() skipped.
  showFallback() {
    this.stopped = true;
    if (this.hasCanvasTarget) this.canvasTarget.classList.add("hidden");
    if (this.hasFallbackTarget) this.fallbackTarget.classList.remove("hidden");
  }
}
